'use client';

import { FetcherContext } from '@copmer/calculator-integration/client';
import { X } from 'lucide-react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';

import { getCurrentUserForPopup } from '@/actions/calculator/user';
import { Dialog, DialogClose, DialogContent } from '@/components/ui/dialog';
import RequestQuotePage from '@/freight/components/RequestQuotePage';
import { RequestQuotePageLoader, RequestQuotePageWrapper } from '@/freight/components/RequestQuotePage.loader';
import { useClientBookableVessels } from '@/hooks/useClientBookableVessels';
import { reactQueryFetcher } from '@/lib/calculator/react-query-fetcher';
import { cn } from '@/lib/css-utils';
import { WithQueryClient } from '@/lib/query-client';

const useUserDetails = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [userDetails, setUserDetails] = useState({
        email: '',
        phoneNumber: '',
    });

    useEffect(() => {
        const fetchUserDetails = async () => {
            try {
                setIsLoading(true);
                const details = await getCurrentUserForPopup();

                if (details.status == 'success') {
                    if (details.data.loggedIn) {
                        setUserDetails({
                            email: details.data.email,
                            phoneNumber: details.data.phoneNumber,
                        });
                    }
                } else {
                    console.error('Failed to fetch user details');
                }
            } finally {
                setIsLoading(false);
            }
        };

        fetchUserDetails();
    }, []);

    return {
        ...userDetails,
        isLoading,
    };
};

export default function PlanQuoteModal({ resultId, planId }: { resultId: string; planId: string }) {
    const router = useRouter();

    const { bookableVessels, isLoading: isVesselsLoading } = useClientBookableVessels();
    const { email, phoneNumber, isLoading } = useUserDetails();

    const [modalHidden, setModalHidden] = useState(false);

    const searchParams = useSearchParams();

    return (
        <Dialog
            open
            onOpenChange={(open) => {
                if (!open) {
                    router.back();
                }
            }}
        >
            <DialogContent
                className={cn('p-0 rounded-lg max-w-[1320px] w-[90%] translate-x-[-50%] max-h-modalH', {
                    'opacity-0 pointer-events-none': modalHidden,
                })}
                noClose
                fullScreen
                manualMaxWidth
                overlayClassName={cn({
                    'opacity-0': modalHidden,
                })}
            >
                <WithQueryClient>
                    <FetcherContext.Provider value={reactQueryFetcher}>
                        {isLoading || isVesselsLoading ? (
                            <RequestQuotePageWrapper isModal subtitle={true}>
                                <RequestQuotePageLoader />
                            </RequestQuotePageWrapper>
                        ) : (
                            <RequestQuotePage
                                resultId={resultId}
                                planId={planId}
                                bookableVessels={bookableVessels}
                                email={email}
                                phoneNumber={phoneNumber}
                                isModal
                                requestClose={() => {
                                    router.back();
                                }}
                                onSuccessChange={(success) => {
                                    setModalHidden(success);
                                }}
                                initialCurrency={searchParams.get('currency') ?? undefined}
                            />
                        )}
                    </FetcherContext.Provider>
                </WithQueryClient>

                <DialogClose className="absolute -right-4 -top-4 rounded-full p-2 bg-frGrey-990 text-white ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
                    <X className="h-4 w-4" />
                    <span className="sr-only">Close</span>
                </DialogClose>
            </DialogContent>
        </Dialog>
    );
}
